import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { SseService } from 'src/app/services/SseService';

/** Agregados de ia_llamadas (totales, por origen o por día), como los devuelve /admin/ia/resumen. */
export interface IaAgregado {
  origen?: string;
  dia?: string;
  llamadas: number;
  errores: number;
  incompletas: number;
  prompt_tokens: number;
  cached_tokens: number;
  completion_tokens: number;
  reasoning_tokens: number;
  total_tokens: number;
  coste_usd: number | null;
  sin_coste: number;
  ms_medio: number | null;
  ms_p95: number | null;
}

export interface IaLlamadaFila {
  id: number;
  fecha: string;
  origen: string;
  idusuario: number | null;
  email: string | null;
  modelo: string;
  reasoning_effort: string;
  duracion_ms: number;
  estado: 'ok' | 'cortada' | 'vacia' | 'error';
  finish_reason: string | null;
  prompt_tokens: number;
  cached_tokens: number;
  completion_tokens: number;
  reasoning_tokens: number;
  total_tokens: number;
  coste_usd: number | null;
}

export interface IaDetalle {
  id: number;
  error: string | null;
  prompt_recortado: string | null;
  respuesta_recortada: string | null;
}

export interface IaPrecio {
  modelo: string;
  entrada_por_millon: number | null;
  cacheada_por_millon: number | null;
  salida_por_millon: number | null;
  actualizado: string | null;
}

function isoDia(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

@Component({
  selector: 'app-admin-ia-uso',
  templateUrl: './admin-ia-uso.component.html',
  styleUrls: ['./admin-ia-uso.component.scss'],
})
export class AdminIaUsoComponent implements OnInit {
  desde = isoDia(new Date(Date.now() - 6 * 86400000));
  hasta = isoDia(new Date());
  origen = '';

  totales: IaAgregado | null = null;
  porOrigen: IaAgregado[] = [];
  llamadas: IaLlamadaFila[] = [];
  precios: IaPrecio[] = [];
  modeloActual = '';
  precioActual = true;

  seleccionada: IaLlamadaFila | null = null;
  detalle: IaDetalle | null = null;
  isLoading = false;
  guardandoPrecio = '';

  constructor(
    private sseService: SseService,
    private cookieService: CookieService,
    private toastr: ToastrService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.cargarPrecios();
  }

  private get token(): string {
    return this.cookieService.get('auth_token');
  }

  private get rango(): string {
    return `desde=${encodeURIComponent(this.desde)}&hasta=${encodeURIComponent(this.hasta)}`;
  }

  cargar(): void {
    this.isLoading = true;
    this.seleccionada = null;
    this.detalle = null;
    this.sseService.apiGetWithAuth(`admin/ia/resumen?${this.rango}`, this.token).subscribe({
      next: (r: any) => {
        this.totales = r?.totales || null;
        this.porOrigen = r?.porOrigen || [];
        this.modeloActual = r?.modeloActual || '';
        this.precioActual = r?.precioActual !== false;
        this.cargarLlamadas();
      },
      error: (e: any) => this.fallo('No fue posible cargar el resumen de la IA', e),
    });
  }

  cargarLlamadas(): void {
    const filtro = this.origen ? `&origen=${encodeURIComponent(this.origen)}` : '';
    this.isLoading = true;
    this.sseService.apiGetWithAuth(`admin/ia/llamadas?${this.rango}${filtro}&limite=200`, this.token).subscribe({
      next: (filas: IaLlamadaFila[]) => {
        this.llamadas = Array.isArray(filas) ? filas : [];
        this.isLoading = false;
        this.cdRef.markForCheck();
      },
      error: (e: any) => this.fallo('No fue posible cargar las llamadas a la IA', e),
    });
  }

  verDetalle(fila: IaLlamadaFila): void {
    if (this.seleccionada?.id === fila.id) {
      this.seleccionada = null;
      this.detalle = null;
      return;
    }
    this.seleccionada = fila;
    this.detalle = null;
    this.sseService.apiGetWithAuth(`admin/ia/llamadas/${fila.id}`, this.token).subscribe({
      next: (d: IaDetalle) => {
        if (this.seleccionada?.id === fila.id) this.detalle = d;
        this.cdRef.markForCheck();
      },
      error: (e: any) => this.fallo('No fue posible cargar el detalle de la llamada', e),
    });
  }

  cargarPrecios(): void {
    this.sseService.apiGetWithAuth('admin/ia/precios', this.token).subscribe({
      next: (filas: IaPrecio[]) => {
        this.precios = Array.isArray(filas) ? filas : [];
        this.cdRef.markForCheck();
      },
      error: (e: any) => this.fallo('No fue posible cargar los precios de la IA', e),
    });
  }

  precioValido(p: IaPrecio): boolean {
    return [p.entrada_por_millon, p.cacheada_por_millon, p.salida_por_millon]
      .every(v => v !== null && v !== undefined && (v as any) !== '' && Number(v) >= 0);
  }

  guardarPrecio(p: IaPrecio): void {
    if (!this.precioValido(p) || this.guardandoPrecio) return;
    this.guardandoPrecio = p.modelo;
    const body = {
      modelo: p.modelo,
      entrada: Number(p.entrada_por_millon),
      cacheada: Number(p.cacheada_por_millon),
      salida: Number(p.salida_por_millon),
    };
    this.sseService.apiPostNew('admin/ia/precios', body, this.token).subscribe({
      next: () => {
        this.guardandoPrecio = '';
        this.toastr.success(`Precio de ${p.modelo} guardado. Se aplica a las llamadas nuevas.`, 'Éxito', { timeOut: 5000 });
        if (p.modelo === this.modeloActual) this.precioActual = true;
        this.cargarPrecios();
      },
      error: (e: any) => {
        this.guardandoPrecio = '';
        this.fallo(e?.error?.message || 'No fue posible guardar el precio', e);
      },
    });
  }

  private fallo(mensaje: string, error: any): void {
    console.error(mensaje, error);
    this.toastr.error(mensaje, 'Error', { timeOut: 5000 });
    this.isLoading = false;
    this.cdRef.markForCheck();
  }
}
